import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../lib/cn.js";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const variantStyles: Record<Variant, string> = {
  primary: "bg-primary text-primary-fg hover:bg-primary/90",
  secondary: "bg-muted text-fg hover:bg-muted/80",
  ghost: "bg-transparent text-fg hover:bg-muted",
  danger: "bg-danger text-white hover:bg-danger/90",
};

const sizeStyles: Record<Size, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-base",
  lg: "h-12 px-6 text-lg",
};

/**
 * Class string for a button-shaped element. Use this on `<Link>` / `<a>`
 * elements that should look like buttons — nesting a `<button>` inside an
 * anchor is invalid HTML and lets clicks/Enter fall through to the anchor
 * even when the button is disabled.
 */
export function buttonClasses(opts: {
  variant?: Variant;
  size?: Size;
  className?: string;
} = {}): string {
  const { variant = "primary", size = "md", className } = opts;
  return cn(
    "inline-flex items-center justify-center rounded-md font-medium transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "disabled:pointer-events-none disabled:opacity-50",
    variantStyles[variant],
    sizeStyles[size],
    className,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", ...props }, ref) => (
    <button
      ref={ref}
      className={buttonClasses({ variant, size, className })}
      {...props}
    />
  ),
);
Button.displayName = "Button";
